import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, MenuItem, TextField } from '@material-ui/core';
import { useField, useFormikContext } from 'formik';
import { showToast } from '@klpkonsern/ui';

import { getFormFieldStyles } from '../formFieldStyles';
import type { FormField } from '../interfaces';
import classNames from 'classnames';
import { useGetKlassifiseringerQuery } from '../services/rtkApi';
import { getLocalizedLabel } from '../utils';

type KlassifiseringItem = {
    id: string;
    navn: string;
};

export const KlassifiseringsSelector = ({
    field,
    isReadOnly,
    isRequired,
}: {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const classes = getFormFieldStyles();
    const [, meta] = useField(field.id);
    const { setFieldValue } = useFormikContext();

    const {
        data,
        isLoading: isLoadingKlassifiseringer,
        isError: isErrorKlassifiseringer,
    } = useGetKlassifiseringerQuery(i18n.language);

    useEffect(() => {
        if (isErrorKlassifiseringer) {
            setFieldValue(field.id, '');
            showToast({
                body: t('leverandor.table.feltOppdateringFeilet', {
                    fieldName: field.label ?? field.id,
                }),
                type: 'error',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isErrorKlassifiseringer]);

    const klassifiseringer: Array<string> | null = Object.values(data ?? {});

    let parsedSelectedValue: KlassifiseringItem | null = null;
    try {
        parsedSelectedValue = meta.value ? JSON.parse(meta.value) : null;
    } catch (error) {
        showToast({
            body: t('leverandor.table.ugyldigJSONBransje'),
            type: 'error',
        });
    }

    const handleChange = (value: string) => {
        const id = Object.entries(data ?? {}).find(
            ([__, val]) => val === value,
        )?.[0];
        if (id) {
            const selectedKlassifisering: KlassifiseringItem = {
                id,
                navn: value,
            };
            setFieldValue(field.id, JSON.stringify(selectedKlassifisering));
        } else {
            showToast({
                body: t('leverandor.table.feltOppdateringFeilet', {
                    fieldName: field.label ?? field.id,
                }),
                type: 'error',
            });
        }
    };

    return (
        <TextField
            id={field.id}
            fullWidth={true}
            className={classNames({
                [classes.editableField]: !isReadOnly,
                [classes.readOnlyField]: isReadOnly,
            })}
            variant="outlined"
            select={true}
            disabled={!!isReadOnly}
            required={isRequired}
            error={meta.touched && Boolean(meta.error)}
            helperText={meta.touched ? meta.error : ''}
            value={parsedSelectedValue?.navn ?? ''}
            onChange={(e) => handleChange(e.target.value)}
            label={getLocalizedLabel('', field?.label, field?.id)}
            InputLabelProps={{
                shrink: true,
            }}
        >
            {isLoadingKlassifiseringer ? (
                <CircularProgress size={20} color="inherit" />
            ) : (
                klassifiseringer?.map((v) => (
                    <MenuItem key={v} value={v}>
                        {v}
                    </MenuItem>
                ))
            )}
        </TextField>
    );
};
